<template>
    <div class="bms-state-list">
        <div class="filter-section">
            <el-form :inline="true" :model="filterForm" class="filter-form">
                <el-form-item label="盒子">
                    <el-select
                        v-model="filterForm.box_id" placeholder="选择盒子"
                        clearable class="wide-select" @change="onBoxChange">
                        <el-option
                            v-for="box in validBoxes"
                            :key="box.id"
                            :label="box.name"
                            :value="box.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="UART端口">
                    <el-select
                        v-model="filterForm.uart_port_id" placeholder="选择UART端口"
                        clearable class="wide-select">
                        <el-option
                            v-for="port in uartPorts"
                            :key="port"
                            :label="`端口 ${port}`"
                            :value="port" />
                    </el-select>
                </el-form-item>
                <el-form-item label="时间范围">
                    <UtcDatePicker
                        v-model="filterForm.dateRange"
                        type="datetimerange"
                        range-separator="至"
                        start-placeholder="开始时间"
                        end-placeholder="结束时间"
                        format="YYYY-MM-DD HH:mm:ss" />
                </el-form-item>
                <el-form-item>
                    <el-button type="primary" @click="loadBmsStates">查询</el-button>
                    <el-button :loading="exporting" @click="exportCsv">导出CSV</el-button>
                    <el-button @click="resetFilter">重置</el-button>
                </el-form-item>
            </el-form>
        </div>

        <div class="table-section">
            <el-table
                v-loading="loading"
                :data="bmsStates"
                stripe
                :default-sort="{ prop: 'update_at', order: 'descending' }">
                <el-table-column prop="box_name" label="盒子名称" />
                <el-table-column prop="uart_port_id" label="UART端口">
                    <template #default="scope">
                        端口 {{ scope.row.uart_port_id }}
                    </template>
                </el-table-column>
                <el-table-column prop="bms_device_id" label="BMS设备ID" />
                <el-table-column prop="voltage" label="电压" sortable>
                    <template #default="scope">
                        <span :class="{ 'abnormal-data': scope.row.is_abnormal.voltage }">
                            {{ formatVoltage(scope.row.voltage) }}
                        </span>
                    </template>
                </el-table-column>
                <el-table-column prop="adjusted_resistance" label="调整后内阻" sortable>
                    <template #default="scope">
                        <span :class="{ 'abnormal-data': scope.row.is_abnormal.resistance }">
                            {{ formatResistance(scope.row.adjusted_resistance) }}
                        </span>
                    </template>
                </el-table-column>
                <el-table-column prop="temperature" label="温度" sortable>
                    <template #default="scope">
                        <span :class="{ 'abnormal-data': scope.row.is_abnormal.temperature }">
                            {{ formatTemperature(scope.row.temperature) }}
                        </span>
                    </template>
                </el-table-column>
                <el-table-column prop="update_at" label="更新时间" sortable>
                    <template #default="scope">
                        {{ formatDateTime(scope.row.update_at) }}
                    </template>
                </el-table-column>
                <el-table-column prop="received_at" label="接收时间" sortable>
                    <template #default="scope">
                        {{ formatDateTime(scope.row.received_at) }}
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <div class="pagination-section">
            <el-pagination
                v-model:current-page="pagination.page"
                v-model:page-size="pagination.rspp"
                :page-sizes="[10, 20, 50, 100]"
                :total="pagination.total"
                layout="total, sizes, prev, pager, next, jumper"
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange" />
        </div>
    </div>
</template>

<script>
import {notify} from '../../assets/js/utils.js';
import UtcDatePicker from '../utils/UtcDatePicker.vue';
import {formatToBJ} from '../../assets/js/time.js';

export default {
    name: 'BmsStateList',
    components: {
        UtcDatePicker,
    },
    data() {
        return {
            loading: false,
            exporting: false,
            bmsStates: [],
            boxes: [],
            uartPorts: [1, 2, 3, 4, 5, 6],
            filterForm: {
                box_id: null,
                uart_port_id: null,
                dateRange: null,
            },
            pagination: {
                page: 1,
                rspp: 100,
                total: 0,
            },
        };
    },
    computed: {
        validBoxes() {
            const boxes = this.boxes;
            if (!Array.isArray(boxes)) {
                return [];
            }
            return boxes.filter(box => box && box.id && box.name);
        },
    },
    mounted() {
        this.loadBoxes();
        this.loadBmsStates();
    },
    methods: {
        async loadBoxes() {
            try {
                const response = await this.axios.get('/api/boxes');
                console.log('Boxes response:', response.data);
                this.boxes = Array.isArray(response.data.data) ? response.data.data : [];
            } catch (error) {
                console.error('加载盒子列表失败:', error);
                notify('error', '加载盒子列表失败');
                this.boxes = [];
            }
        },

        async loadBmsStates() {
            this.loading = true;
            try {
                const params = {
                    page: this.pagination.page,
                    rspp: this.pagination.rspp,
                };

                if (this.filterForm.box_id) {
                    params.box_id = this.filterForm.box_id;
                }

                if (this.filterForm.uart_port_id) {
                    params.uart_port_id = this.filterForm.uart_port_id;
                }

                if (this.filterForm.dateRange && this.filterForm.dateRange.length === 2) {
                    params.start_time = this.filterForm.dateRange[0];
                    params.end_time = this.filterForm.dateRange[1];
                }

                const response = await this.axios.get('/api/bms-states', {params});
                this.bmsStates = response.data.data;
                this.pagination.total = response.data.pagination.total;
            } catch (error) {
                console.error('加载BMS状态数据失败:', error);
                notify('error', '加载BMS状态数据失败');
            } finally {
                this.loading = false;
            }
        },

        async exportCsv() {
            this.exporting = true;
            try {
                const params = {};

                if (this.filterForm.box_id) {
                    params.box_id = this.filterForm.box_id;
                }

                if (this.filterForm.uart_port_id) {
                    params.uart_port_id = this.filterForm.uart_port_id;
                }

                if (this.filterForm.dateRange && this.filterForm.dateRange.length === 2) {
                    params.start_time = this.filterForm.dateRange[0];
                    params.end_time = this.filterForm.dateRange[1];
                }

                const response = await this.axios.get('/api/bms-states/export', {
                    params,
                    responseType: 'blob',
                });

                const blob = new Blob([response.data], {type: 'text/csv;charset=utf-8'});
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;

                const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
                link.download = `bms_states_${timestamp}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

                notify('success', '导出成功');
            } catch (error) {
                console.error('导出失败:', error);
                notify('error', '导出失败');
            } finally {
                this.exporting = false;
            }
        },

        onBoxChange() {
            // 重置UART端口选择
            this.filterForm.uart_port_id = null;
        },

        resetFilter() {
            this.filterForm = {
                box_id: null,
                uart_port_id: null,
                dateRange: null,
            };
            this.pagination.page = 0;
            this.loadBmsStates();
        },

        handleSizeChange(size) {
            this.pagination.rspp = size;
            this.pagination.page = 0;
            this.loadBmsStates();
        },

        handleCurrentChange(page) {
            this.pagination.page = page;
            this.loadBmsStates();
        },

        formatVoltage(value) {
            return value ? `${(value / 1000).toFixed(2)} V` : '-';
        },

        formatResistance(value) {
            return value ? `${(value / 1000).toFixed(2)} mΩ` : '-';
        },

        formatTemperature(value) {
            return value ? `${((value - 400) / 10).toFixed(1)} °C` : '-';
        },

        formatDateTime(value) {
            return formatToBJ(value);
        },
    },
};
</script>

<style scoped>
.bms-state-list {
    padding: 20px;
}

.filter-section {
    margin-bottom: 20px;
    padding: 15px;
    background-color: #f5f5f5;
    border-radius: 4px;
}

.filter-form {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.wide-select {
    min-width: 180px;
}

.table-section {
    margin-bottom: 20px;
}

.table-section :deep(.el-table) {
    font-family: monospace;
}

.table-section :deep(.el-table th) {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: bold;
}

.table-section :deep(.el-table td) {
    padding: 8px 12px;
}

.pagination-section {
    display: flex;
    justify-content: center;
}

.abnormal-data {
    color: #f56c6c;
    font-weight: bold;
}
</style>
